import React, { useEffect, useState, useCallback } from 'react';
import api from '@/services/api';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  ChatCircle, Users, Funnel, Robot, CalendarBlank, Gear, ChartBar,
  EnvelopeSimple, Lightning, MagnifyingGlass, Fire, ArrowRight,
  BellRinging, Car, Notebook, Trophy, UsersThree
} from '@phosphor-icons/react';

const PAGES = [
  { name: 'Dashboard', path: '/dashboard', icon: ChartBar, keywords: 'home overview stats' },
  { name: 'Inbox', path: '/inbox', icon: ChatCircle, keywords: 'messages conversations chat' },
  { name: 'Contacts', path: '/contacts', icon: Users, keywords: 'customers leads people' },
  { name: 'Pipeline', path: '/pipeline', icon: Funnel, keywords: 'deals stages kanban' },
  { name: 'Inventory', path: '/inventory', icon: Car, keywords: 'vehicles cars trucks' },
  { name: 'AI Agents', path: '/agents', icon: Robot, keywords: 'bots lotmate ai' },
  { name: 'Action Center', path: '/followups', icon: BellRinging, keywords: 'followups tasks' },
  { name: 'Calendar', path: '/calendar', icon: CalendarBlank, keywords: 'appointments schedule' },
  { name: 'Campaigns', path: '/campaigns', icon: EnvelopeSimple, keywords: 'sms email blast' },
  { name: 'Templates', path: '/templates', icon: Notebook, keywords: 'sms templates' },
  { name: 'Users & Notifications', path: '/team', icon: UsersThree, keywords: 'team users roles' },
  { name: 'Leaderboard', path: '/leaderboard', icon: Trophy, keywords: 'rankings performance' },
  { name: 'Settings', path: '/settings', icon: Gear, keywords: 'preferences config' },
];

const QUICK_ACTIONS = [
  { name: 'New Chat', desc: 'Start a new conversation', path: '/inbox', icon: ChatCircle },
  { name: 'Add Contact', desc: 'Create a new contact', path: '/contacts', icon: Users },
  { name: 'View Hot Leads', desc: 'See all hot leads', path: '/contacts?filter=hot', icon: Fire },
  { name: 'Action Center', desc: 'View pending actions', path: '/followups', icon: BellRinging },
];

export const GlobalCommandBar = () => {
  const [open, setOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const navigate = useNavigate();

  // Keyboard shortcut
  useEffect(() => {
    const down = (e) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(prev => !prev);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  // Fetch contacts when opened
  useEffect(() => {
    if (open) {
      api.get('/contacts', {
        params: {
          page: 1,
          page_size: 10,
        },
      })
        .then((res) => {
          const rows = Array.isArray(res.data) ? res.data : (res.data?.items || []);
          setContacts(rows.slice(0, 10));
        })
        .catch(() => {});
    }
  }, [open]);

  const go = (path) => {
    if (path.includes('?')) {
      const [pathname, query] = path.split('?');
      navigate({ pathname: pathname.startsWith('/') ? pathname : `/${pathname}`, search: `?${query}` });
    } else {
      navigate(path);
    }
    setOpen(false);
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search contacts, pages, actions..." data-testid="command-input" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        <CommandGroup heading="Quick Actions">
          {QUICK_ACTIONS.map(action => (
            <CommandItem key={action.name} onSelect={() => go(action.path)} data-testid={`cmd-action-${action.name.toLowerCase().replace(/\s/g, '-')}`}>
              <action.icon size={14} className="mr-2 text-muted-foreground" />
              <div>
                <span className="text-sm">{action.name}</span>
                <span className="text-[10px] text-muted-foreground ml-2">{action.desc}</span>
              </div>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        {contacts.length > 0 && (
          <>
            <CommandGroup heading="Contacts">
              {contacts.map(c => (
                <CommandItem key={c.id} onSelect={() => go(`/inbox/${c.id}`)} data-testid={`cmd-contact-${c.id}`}>
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center mr-2 shrink-0">
                    <span className="text-[9px] font-bold text-primary">{c.name?.charAt(0)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm">{c.name}</span>
                    <span className="text-[10px] text-muted-foreground ml-2">{c.phone || c.email || ''}</span>
                  </div>
                  {c.customer_type === 'hot' && <Fire size={12} className="text-red-500 shrink-0" />}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        <CommandGroup heading="Pages">
          {PAGES.map(page => (
            <CommandItem key={page.path} onSelect={() => go(page.path)} keywords={[page.keywords]} data-testid={`cmd-page-${page.name.toLowerCase().replace(/\s/g, '-')}`}>
              <page.icon size={14} className="mr-2 text-muted-foreground" />
              <span className="text-sm">{page.name}</span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};
